import { createElement, ReactNode } from 'react';
import { /*ToastContainer,*/ toast, ToastOptions } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CopyToast, { CopyToastIcon, CopyToastProps } from './CopyToast';

interface AlertContent {
  header: string;
  description?: string;
}

type AlertMessage = string | ReactNode | AlertContent;

const isAlertContent = (message: AlertMessage): message is AlertContent =>
  typeof message === 'object' && message !== null && 'header' in message;

const renderAlertContent = (message: AlertMessage): ReactNode => {
  if (isAlertContent(message)) {
    const { header, description } = message;
    return (
      <div>
        <div className="fw-bold">{header}</div>
        {description && <div className="small">{description}</div>}
      </div>
    );
  }
  return message;
};

const DEFAULT_TOAST_OPTIONS: ToastOptions = {
  position: 'top-right',
  autoClose: 5000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

export const showSuccess = (message: AlertMessage, options?: ToastOptions) =>
  toast.success(renderAlertContent(message), { ...DEFAULT_TOAST_OPTIONS, ...options });

export const showError = (message: AlertMessage, options?: ToastOptions) =>
  toast.error(renderAlertContent(message), { ...DEFAULT_TOAST_OPTIONS, ...options });

export const showInfo = (message: AlertMessage, options?: ToastOptions) =>
  toast.info(renderAlertContent(message), { ...DEFAULT_TOAST_OPTIONS, ...options });

/**
 * A copy confirmation: the label, the value, and the brand's own mark.
 *
 * `createElement` rather than JSX on purpose — this module is the app's toast
 * API, not a component. The toast's body and icon live in CopyToast.tsx.
 */
export const showCopied = ({ mark, icon, label, value }: CopyToastProps) =>
  toast.success(createElement(CopyToast, { label, value }), {
    ...DEFAULT_TOAST_OPTIONS,
    // Render-function form: react-toastify types the element form against its own
    // IconProps, which a brand mark doesn't take.
    icon: () => createElement(CopyToastIcon, { mark, icon }),
  });
